
import Link from '@/components/compat/Link';
import { Calendar, Tag, Store } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  type PowerInfo,
  getPowerCategoryLabel,
  formatReleaseDate,
  formatStorePrice,
  formatFairtradePrice,
} from '@/lib/powers-data';
import { getPowerCategoryAccentHex } from '@/lib/power-category-theme';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { GsImage, MoXat, cn } from '@/components/help-center/latest-power-shared';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { PowerCtaArrow } from '@/components/help-center/powers/PowerCtaArrow';
import PowerDarkCardShell from '@/components/help-center/powers/PowerDarkCardShell';

const powerInfoRowClass = cn(
  'flex w-full min-h-[36px] items-center justify-between gap-2 rounded-xl px-3 py-2',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-colors duration-200 group-hover:border-sky-400/25 group-hover:from-white/[0.09]'
);

export default function PowerCard({
  power,
  overlay,
  liftOnHover = true,
}: {
  power: PowerInfo;
  /** Conteúdo absoluto (ex.: botão fechar) que sobe/desce junto com o card no hover. */
  overlay?: ReactNode;
  liftOnHover?: boolean;
}) {
  const categoryColor = getPowerCategoryAccentHex(power.category);
  const displayName = formatPowerDisplayName(power.slug);

  return (
    <div
      className={cn(
        'group relative block',
        liftOnHover && 'transition-transform duration-300 hover:-translate-y-1'
      )}
    >
      {overlay}
      <Link href={`/powers/${power.slug}`} className="block">
        <PowerDarkCardShell
          className="h-full transition-all duration-300 hover:border-sky-500/40 hover:ring-sky-500/30 hover:shadow-blue-950/50"
          innerClassName="flex flex-col px-4 pb-4 pt-4"
        >
        {/* Icon + ID */}
        <div className="mb-3 flex flex-col items-center gap-2.5">
          <div className="relative">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-200 group-hover:scale-105"
              style={{
                background:
                  'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
                boxShadow:
                  '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70, 6px 10px 22px rgba(0,0,0,0.55)',
              }}
            >
              <GsImage
                code={power.slug}
                className="flex h-full w-full items-center justify-center"
                imgClassName="max-h-[52px] max-w-[52px] object-contain"
              />
            </div>
            {power.id > 0 && (
              <span
                className="pointer-events-none absolute -top-2 -right-2.5 inline-flex items-center rounded-full px-2 py-0.5 font-poppins text-[10px] font-bold tabular-nums shadow-sm"
                style={{
                  background: 'linear-gradient(135deg, #0e1f7a 0%, #091460 100%)',
                  color: '#8aabf8',
                  boxShadow: '0 0 0 1.5px #1a3099',
                }}
              >
                ID {power.id}
              </span>
            )}
          </div>

          <span
            className="font-poppins text-lg font-semibold tracking-tight text-white leading-none text-center"
            style={{
              textShadow: '0 0 20px rgba(255,255,255,0.28), 0 0 6px rgba(255,255,255,0.12)',
            }}
          >
            {displayName}
          </span>

          {power.limited ? (
            <span
              className="inline-flex items-center rounded-full px-3.5 py-1 font-poppins text-[10px] font-bold uppercase tracking-widest text-white"
              style={{
                background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
                boxShadow: '0 0 0 1.5px rgba(240,130,30,0.40), 0 4px 12px rgba(220,90,10,0.30)',
              }}
            >
              Limitado
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-3.5 py-1 font-poppins text-[10px] font-bold uppercase tracking-widest text-cyan-300 ring-1 ring-cyan-400/35">
              Ilimitado
            </span>
          )}
        </div>

        {/* Info rows */}
        <div className="mb-3 space-y-1.5">
          <div className={powerInfoRowClass}>
            <span className="flex min-w-0 items-center gap-1.5 font-poppins text-[9px] font-semibold uppercase tracking-wide text-sky-200/75">
              <Calendar className="h-3 w-3 shrink-0 text-sky-300/90" aria-hidden />
              Lançamento:
            </span>
            <span className="min-w-0 text-right font-poppins text-[10px] font-semibold text-blue-50">
              {formatReleaseDate(power.released)}
            </span>
          </div>
          <div className={powerInfoRowClass}>
            <span className="flex min-w-0 items-center gap-1.5 font-poppins text-[9px] font-semibold uppercase tracking-wide text-sky-200/75">
              <Tag className="h-3 w-3 shrink-0 text-sky-300/90" aria-hidden />
              Categoria:
            </span>
            <span
              className="min-w-0 text-right font-poppins text-[10px] font-semibold"
              style={{ color: categoryColor }}
            >
              {getPowerCategoryLabel(power.category)}
            </span>
          </div>
          <div className={powerInfoRowClass}>
            <span className="flex min-w-0 items-center gap-1.5 font-poppins text-[9px] font-semibold uppercase tracking-wide text-sky-200/75">
              <Store className="h-3 w-3 shrink-0 text-sky-300/90" aria-hidden />
              Loja:
            </span>
            <span className="inline-flex min-w-0 items-center justify-end gap-1 font-poppins text-[10px] font-semibold text-blue-50">
              <MoXat />
              {formatStorePrice(power.prices.store)}
            </span>
          </div>
          <div className={powerInfoRowClass}>
            <span className="flex min-w-0 items-center gap-1.5 font-poppins text-[9px] font-semibold uppercase tracking-wide text-sky-200/75">
              <img
                src="/icons/fairtrade.svg"
                alt=""
                width={12}
                height={12}
                className="h-3 w-3 shrink-0 object-contain opacity-90"
                aria-hidden
              />
              Fairtrade:
            </span>
            <span className="inline-flex min-w-0 items-center justify-end gap-1 font-poppins text-[10px] font-semibold text-blue-50">
              <MoXat />
              {formatFairtradePrice(power.prices.fairtrade_min, power.prices.fairtrade_max)}
            </span>
          </div>
        </div>

        <div className="mb-3 px-0.5">
          <PortalCrystalSeparator accentRgb="56, 189, 248" />
        </div>

        {/* CTA */}
        <div
          className={cn(
            'mt-auto inline-flex w-full items-center justify-center gap-2.5 rounded-xl px-4 py-2.5',
            'bg-white/[0.07] ring-1 ring-white/[0.12]',
            'font-poppins text-xs font-semibold text-white',
            'transition-all duration-200 group-hover:bg-white/[0.12] group-hover:ring-white/20 group-hover:text-sky-100'
          )}
        >
          <span>Ver power</span>
          <PowerCtaArrow className="h-5 w-5" />
        </div>
      </PowerDarkCardShell>
      </Link>
    </div>
  );
}
